<div class="page">
  <!-- ===================== HEADER ===================== -->
  <div class="page-head">
    <div>
      <h2>{{ pageTitle() || 'Partner Records' }}</h2>
      <p>Insured members and payment transactions for the selected period.</p>
    </div>
    <!-- Role dispatcher, not the partner page — an admin lands on the Admin Dashboard. -->
    <p-button label="Back to Dashboard" icon="pi pi-arrow-left" [outlined]="true" routerLink="/dashboard" />
  </div>

  <!-- ===================== FILTER BAR ===================== -->
  <app-partner-filter-bar [isAdmin]="isAdmin" [partnerOptions]="partnerOptions()" (filterChange)="onFilter($event)" />

  <!-- ===================== TABS ===================== -->
  <div class="card tabs-card">
    <p-tabs [value]="activeTab()" (valueChange)="activeTab.set($any($event))">
      <p-tablist>
        <p-tab value="members"><i class="pi pi-users"></i> Insured Members</p-tab>
        <p-tab value="done"><i class="pi pi-check-circle"></i> Payment Done</p-tab>
        <p-tab value="failed"><i class="pi pi-times-circle"></i> Not Successful</p-tab>
      </p-tablist>
      <p-tabpanels>
        <!-- ---------- Insured Members ---------- -->
        <p-tabpanel value="members">
          <div class="toolbar">
            <p-iconfield iconPosition="left" class="search-field">
              <p-inputicon styleClass="pi pi-search" />
              <input type="text" pInputText placeholder="Search members..." [ngModel]="members.search()"
                (ngModelChange)="onSearch('members', $event)" />
            </p-iconfield>
            <div class="filter">
              <p-select [ngModel]="members.status()" (ngModelChange)="members.status.set($event); onTableFilter('members')"
                [options]="paymentStatusOptions" optionLabel="label" optionValue="value" [showClear]="true"
                placeholder="Payment status" appendTo="body" />
            </div>
            <div class="filter">
              <p-select [ngModel]="members.kyc()" (ngModelChange)="members.kyc.set($event); onTableFilter('members')"
                [options]="kycStatusOptions" optionLabel="label" optionValue="value" [showClear]="true"
                placeholder="KYC status" appendTo="body" />
            </div>
            <span class="count">{{ members.total() }} total</span>
          </div>

          <p-table [value]="members.rows()" [loading]="members.loading()" dataKey="id" [lazy]="true"
            [lazyLoadOnInit]="false" [paginator]="true" [rows]="members.pageSize()" [first]="members.first()"
            [totalRecords]="members.total()" [rowsPerPageOptions]="[10, 25, 50]"
            (onLazyLoad)="onMembersLazy($event)" styleClass="p-datatable-sm"
            currentPageReportTemplate="Showing {first}–{last} of {totalRecords}" [showCurrentPageReport]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>Member</th>
                <th>Mobile</th>
                <th>Policy</th>
                <th class="ta-r">Premium</th>
                <th>Payment</th>
                <th>KYC</th>
                <th>Paid At</th>
                <th>Created</th>
                <th class="ta-r">Action</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-r>
              <tr>
                <td>
                  <div class="member-cell">
                    <span class="avatar">{{ initials(r.fullName) }}</span>
                    <div class="member-meta">
                      <span class="m-name">{{ r.fullName || '—' }}</span>
                      <span class="m-sub mono">{{ r.memberSeriesNo || '—' }}</span>
                    </div>
                  </div>
                </td>
                <td class="mono">{{ r.mobileNumber || '—' }}</td>
                <td>
                  @if (r.masterPolicyName) { <span class="policy-chip">{{ r.masterPolicyName }}</span> }
                  @else { <span class="muted">—</span> }
                </td>
                <td class="ta-r amount">{{ inr(r.grossAmt) }}</td>
                <td><p-tag [value]="r.paymentStatus || '—'" [severity]="paySeverity(r.paymentStatus)" /></td>
                <td><p-tag [value]="r.kycStatus || '—'" [severity]="kycSeverity(r.kycStatus)" /></td>
                <td class="created">{{ r.paidAt ? (r.paidAt | indDate) : '—' }}</td>
                <td class="created">{{ r.createdAt ? (r.createdAt | indDate) : '—' }}</td>
                <td class="ta-r">
                  @if (needsPayment(r.paymentStatus)) {
                    <p-button label="Collect" icon="pi pi-credit-card" size="small" [rounded]="true"
                      (onClick)="collect({ id: r.id, name: r.fullName, mobile: r.mobileNumber, email: r.email, amount: r.grossAmt, kycStatus: r.kycStatus })" />
                  } @else if (isIssued(r.paymentStatus)) {
                    <span class="ok-pill"><i class="pi pi-check-circle"></i> Paid</span>
                    <p-button icon="pi pi-receipt" size="small" [rounded]="true" [text]="true"
                      pTooltip="View receipt" (onClick)="viewReceiptForMember(r.id)" />
                    <p-button icon="pi pi-id-card" size="small" [rounded]="true" [text]="true"
                      pTooltip="View certificate" (onClick)="viewCoi(r.id)" />
                    <p-button icon="pi pi-send" size="small" [rounded]="true" [text]="true"
                      pTooltip="Send the certificate to the customer (WhatsApp / SMS / Email)"
                      (onClick)="sendCoi(r.id)" />
                  } @else {
                    <span class="muted">—</span>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="9"><div class="state"><i class="pi pi-users"></i><p>No insured members found.</p></div></td></tr>
            </ng-template>
          </p-table>
        </p-tabpanel>

        <!-- ---------- Payment Done ---------- -->
        <p-tabpanel value="done">
          <div class="toolbar">
            <p-iconfield iconPosition="left" class="search-field">
              <p-inputicon styleClass="pi pi-search" />
              <input type="text" pInputText placeholder="Search successful payments..." [ngModel]="done.search()"
                (ngModelChange)="onSearch('done', $event)" />
            </p-iconfield>
            <span class="count">{{ done.total() }} total</span>
          </div>

          <p-table [value]="done.rows()" [loading]="done.loading()" dataKey="id" [lazy]="true"
            [lazyLoadOnInit]="false" [paginator]="true" [rows]="done.pageSize()" [first]="done.first()"
            [totalRecords]="done.total()" [rowsPerPageOptions]="[10, 25, 50]"
            (onLazyLoad)="onDoneLazy($event)" styleClass="p-datatable-sm"
            currentPageReportTemplate="Showing {first}–{last} of {totalRecords}" [showCurrentPageReport]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>Payment Uid</th>
                <th>Member</th>
                <th>Series No</th>
                <th class="ta-r">Amount</th>
                <th>Mode</th>
                <th>Gateway</th>
                <th>Paid At</th>
                <th>Status</th>
                <th class="ta-r">Actions</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-r>
              <tr>
                <td class="mono">{{ r.paymentUniqueId || '—' }}</td>
                <td class="name">{{ r.memberName || ('Member #' + r.memberId) }}</td>
                <td class="mono">{{ r.memberSeriesNo || '—' }}</td>
                <td class="ta-r amount">{{ inr(r.amount) }}</td>
                <td>{{ r.paymentMode || '—' }}</td>
                <td>{{ r.gatewayName || '—' }}</td>
                <td>{{ paidDate(r) ? (paidDate(r) | indDate) : '—' }}</td>
                <td><p-tag [value]="r.paymentStatus || 'SUCCESS'" [severity]="paySeverity(r.paymentStatus)" /></td>
                <td class="ta-r">
                  <p-button icon="pi pi-receipt" size="small" [rounded]="true" [text]="true"
                    pTooltip="View receipt" (onClick)="viewReceipt(r.id)" />
                  <p-button icon="pi pi-download" size="small" [rounded]="true" [text]="true" severity="secondary"
                    pTooltip="Download receipt" (onClick)="downloadReceipt(r.id, r.paymentUniqueId)" />
                  <p-button icon="pi pi-id-card" size="small" [rounded]="true" [text]="true"
                    pTooltip="View certificate" (onClick)="viewCoi(r.memberId)" />
                  <p-button icon="pi pi-send" size="small" [rounded]="true" [text]="true"
                    pTooltip="Send certificate to the customer" (onClick)="sendCoi(r.memberId)" />
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="9"><div class="state"><i class="pi pi-inbox"></i><p>No successful payments found.</p></div></td></tr>
            </ng-template>
          </p-table>
        </p-tabpanel>

        <!-- ---------- Not Successful ---------- -->
        <p-tabpanel value="failed">
          <div class="toolbar">
            <p-iconfield iconPosition="left" class="search-field">
              <p-inputicon styleClass="pi pi-search" />
              <input type="text" pInputText placeholder="Search failed/pending payments..." [ngModel]="failed.search()"
                (ngModelChange)="onSearch('failed', $event)" />
            </p-iconfield>
            <div class="filter">
              <p-select [ngModel]="failed.status()" (ngModelChange)="failed.status.set($event); onTableFilter('failed')"
                [options]="failedStatusOptions" optionLabel="label" optionValue="value" [showClear]="true"
                placeholder="All non-success" appendTo="body" />
            </div>
            <span class="count">{{ failed.total() }} total</span>
          </div>

          <p-table [value]="failed.rows()" [loading]="failed.loading()" dataKey="id" [lazy]="true"
            [lazyLoadOnInit]="false" [paginator]="true" [rows]="failed.pageSize()" [first]="failed.first()"
            [totalRecords]="failed.total()" [rowsPerPageOptions]="[10, 25, 50]"
            (onLazyLoad)="onFailedLazy($event)" styleClass="p-datatable-sm"
            currentPageReportTemplate="Showing {first}–{last} of {totalRecords}" [showCurrentPageReport]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>Payment Uid</th>
                <th>Member</th>
                <th>Series No</th>
                <th class="ta-r">Amount</th>
                <th>Mode</th>
                <th>Gateway</th>
                <th>Paid At</th>
                <th>Status</th>
                <th>Failure Reason</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-r>
              <tr>
                <td class="mono">{{ r.paymentUniqueId || '—' }}</td>
                <td class="name">{{ r.memberName || ('Member #' + r.memberId) }}</td>
                <td class="mono">{{ r.memberSeriesNo || '—' }}</td>
                <td class="ta-r amount">{{ inr(r.amount) }}</td>
                <td>{{ r.paymentMode || '—' }}</td>
                <td>{{ r.gatewayName || '—' }}</td>
                <td>{{ paidDate(r) ? (paidDate(r) | indDate) : '—' }}</td>
                <td><p-tag [value]="r.paymentStatus || '—'" [severity]="paySeverity(r.paymentStatus)" /></td>
                <td class="reason">{{ r.failureReason || '—' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="9"><div class="state"><i class="pi pi-inbox"></i><p>No unsuccessful payments found.</p></div></td></tr>
            </ng-template>
          </p-table>
        </p-tabpanel>
      </p-tabpanels>
    </p-tabs>
  </div>

  <!-- inline collect-payment flow: opens the gateway this partner is assigned, in place -->
  <app-collect-payment-flow (paid)="onPaid()" />
</div>

<!-- ===================== RECEIPT DIALOG (shared by PAID members / successful payments) ===================== -->
<p-dialog [visible]="showReceipt()" (visibleChange)="showReceipt.set($event)" [modal]="true"
  [draggable]="true" [resizable]="false" [dismissableMask]="true" header="Payment Receipt"
  styleClass="menu-dialog" [style]="{ width: '720px' }" [breakpoints]="{ '720px': '94vw' }">
  @if (showReceipt()) {
    <app-receipt-view [paymentId]="receiptPaymentId()" />
  }
</p-dialog>

<!-- ===================== CERTIFICATE DIALOG (shared by PAID members / successful payments) ===================== -->
<p-dialog [visible]="showCoi()" (visibleChange)="showCoi.set($event)" [modal]="true"
  [draggable]="true" [resizable]="false" [dismissableMask]="true" header="Certificate of Insurance"
  styleClass="menu-dialog" [style]="{ width: '900px' }" [breakpoints]="{ '960px': '96vw' }">
  @if (showCoi()) {
    <app-coi-view [memberId]="coiMemberId()" />
  }
</p-dialog>

<!-- ===================== SEND CERTIFICATE (queued WhatsApp / SMS / Email delivery) ===================== -->
<app-send-coi-dialog [(visible)]="showSendCoi" [memberId]="sendCoiMemberId()" />
